'use client';

import { useRef, useEffect } from 'react';
import Link from 'next/link';
import { useMagnetic } from '@/lib/hooks/useMagnetic';
import { company } from '@/lib/company';
import { HeroStatusLine } from '@/components/effects/HeroStatusLine';

// Hover effect for title letters. GSAP is injected so this module doesn't
// pull GSAP into the homepage entry chunk (TASK-4).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyHoverEffect(letters: NodeListOf<Element>, gsap: any) {
  const spansArray = Array.from(letters);

  const handleMouseLeave = () => {
    spansArray.forEach((span) => {
      gsap.to(span, { scaleY: 1, duration: 0.5, ease: 'power4.out' });
    });
  };

  const handleMouseMoveObj = (e: Event) => {
    const mouseEvent = e as MouseEvent;
    const hoveredSpan = mouseEvent.target as HTMLElement;
    const rect = hoveredSpan.getBoundingClientRect();
    const mouseX = mouseEvent.clientX - rect.left;
    const scaleFactor = 0.2;
    const center = rect.width / 2;

    let scale: number;
    if (mouseX < center) {
      scale = (scaleFactor + 1) + (scaleFactor * mouseX) / center;
    } else {
      scale = (scaleFactor + 1) + (scaleFactor * (rect.width - mouseX)) / center;
    }

    gsap.to(hoveredSpan, { scaleY: scale, duration: 0.5, ease: 'power4.out' });

    const hoveredIndex = spansArray.indexOf(hoveredSpan);
    const prevSpan = spansArray[hoveredIndex - 1];
    const nextSpan = spansArray[hoveredIndex + 1];

    if (prevSpan) {
      const dist = Math.min(Math.abs(rect.left - mouseEvent.clientX), center);
      const scalePrev = 1 + (scaleFactor * (center - dist)) / center;
      gsap.to(prevSpan, { scaleY: scalePrev, duration: 0.5, ease: 'power4.out' });
    }

    if (nextSpan) {
      const dist = Math.min(Math.abs(rect.right - mouseEvent.clientX), center);
      const scaleNext = 1 + (scaleFactor * (center - dist)) / center;
      gsap.to(nextSpan, { scaleY: scaleNext, duration: 0.5, ease: 'power4.out' });
    }
  };

  spansArray.forEach((span) => {
    span.addEventListener('mousemove', handleMouseMoveObj);
    span.addEventListener('mouseleave', handleMouseLeave);
  });
}

export function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLButtonElement>(null);
  const magneticRef = useMagnetic<HTMLDivElement>(0.3);

  useEffect(() => {
    if (!heroRef.current) return;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    let ctx: { revert: () => void } | undefined;
    let cancelled = false;

    (async () => {
      const { gsap } = await import('@/lib/gsap');
      if (cancelled) return;

      ctx = gsap.context(() => {
      // Hero is rendered visible by default so the H1 is LCP-eligible without
      // waiting on JS. The shuffle animation is a decorative overlay we add
      // *after* paint, gated on reduced-motion.
      const letters = titleRef.current?.querySelectorAll('.letter');
      if (letters && !prefersReducedMotion) {
        const shuffledLetters = Array.from(letters).sort(() => Math.random() - 0.5);
        const letterTimeline = gsap.timeline();
        shuffledLetters.forEach((letter, index) => {
          letterTimeline.fromTo(
            letter,
            { scaleY: 0.5 },
            { duration: 0.5, scaleY: 1, ease: 'power3.out' },
            index * 0.04,
          );
        });
        letterTimeline.call(() => {
          applyHoverEffect(letters, gsap);
        });
      } else if (letters) {
        applyHoverEffect(letters, gsap);
      }

      // Subtitle: visible by default; only a small slide-in enhancement if motion is OK
      if (subtitleRef.current && !prefersReducedMotion) {
        gsap.fromTo(
          subtitleRef.current,
          { y: 16, opacity: 0.25 },
          { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' },
        );
      }

      // Scroll indicator: visible by default; gentle nudge if motion is OK
      if (scrollRef.current && !prefersReducedMotion) {
        gsap.fromTo(
          scrollRef.current,
          { y: 12, opacity: 0.4 },
          { y: 0, opacity: 1, duration: 0.7, ease: 'power2.out' },
        );
      }

      // Scroll parallax on the whole hero content (parallax-scroll-caption)
      // Title and content move up faster than background when scrolling
      if (heroRef.current) {
        const contentEl = heroRef.current.querySelector('.hero-content-wrap');
        if (contentEl) {
          gsap.to(contentEl, {
            yPercent: -30,
            ease: 'none',
            scrollTrigger: {
              trigger: heroRef.current,
              start: 'top top',
              end: 'bottom top',
              scrub: true,
            },
          });
        }
      }

      return undefined;
    }, heroRef);
    })();

    return () => {
      cancelled = true;
      ctx?.revert();
    };
  }, []);

  const title = company.wordmark;

  return (
    <section
      ref={heroRef}
      id="hero"
      className="self-styled relative flex min-h-[100dvh] w-full max-w-[100vw] items-center justify-center overflow-x-hidden overflow-y-visible bg-transparent"
    >
        <div className="absolute inset-0 -z-10" aria-hidden="true">
          <img
            src="/images/brand/dilacode-studio-hero.webp"
            alt=""
            className="h-full w-full object-cover object-center opacity-30"
          />
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(23,56,53,0.97)_0%,rgba(23,56,53,0.74)_50%,rgba(23,56,53,0.36)_100%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(23,56,53,0.28)_0%,rgba(23,56,53,0.12)_52%,rgba(23,56,53,0.88)_100%)]" />
        </div>

      {/* Main Content - Centered but pushed down slightly */}
      <div className="hero-content-wrap relative z-10 flex w-full max-w-full flex-col items-center justify-center overflow-visible px-5 text-center md:px-12 lg:px-16 mt-[2vh] md:mt-[8vh]">
        {/* Hero Title — DILACODE wordmark */}
        <h1
          ref={titleRef}
          aria-label={title}
          className="hero-title font-six-caps max-w-full text-[clamp(56px,18vw,134px)] leading-[0.75] tracking-[0.25em] text-white lowercase whitespace-nowrap sm:text-[clamp(96px,17vw,168px)] md:text-[189px] md:tracking-[0.18em] lg:text-[calc(1rem+20vw)]"
        >
          {title.split('').map((char, i) => (
            <span
              key={i}
              aria-hidden="true"
              className="letter inline-block"
              style={{ transformOrigin: 'center bottom' }}
            >
              {char}
            </span>
          ))}
        </h1>

        {/* Explicit spacer to force gap regardless of h1 bounding box bleeding */}
        <div className="h-6 md:h-12 w-full flex-shrink-0" />

        {/* Hero Subtitle */}
        <div className="relative mt-4 w-full max-w-sm cursor-default text-center md:mt-0 md:max-w-3xl">
          {/* Mobile Text - Always visible, standard elegant font */}
          <div className="md:hidden w-full text-sm sm:text-base font-light tracking-wide text-white/85 leading-relaxed">
            DilaCode is a Canadian software company. We build our own business
            software and develop products for clients.
          </div>

          {/* Desktop Base Text - visible initially, elegant and slightly dim */}
          <div
            ref={subtitleRef}
            className="hidden md:block text-base md:text-lg lg:text-xl font-light tracking-wide leading-normal text-white/85 md:leading-snug"
          >
            A Canadian software company.<br />
            We build business software of our own<br />
            and develop products for clients.
          </div>

        </div>

        {/* CTA row — one primary intent, one supporting route to the evidence */}
        <div className="mt-10 flex w-full max-w-sm flex-col items-stretch justify-center gap-3 sm:w-auto sm:max-w-none sm:flex-row sm:items-center sm:gap-4 md:mt-14">
          <Link
            href="/contact"
            className="inline-flex min-h-12 items-center justify-center gap-3 rounded-full px-7 py-3.5 text-center text-[12px] font-semibold uppercase tracking-[0.13em] text-black transition-transform duration-300 hover:scale-[1.03] sm:text-[13px] sm:tracking-[0.16em]"
            style={{ backgroundColor: 'var(--color-accent)' }}
          >
            Start a conversation
          </Link>
          <Link
            href="/company"
            className="inline-flex min-h-12 items-center justify-center gap-3 rounded-full border border-white/30 px-7 py-3.5 text-center text-[12px] font-medium uppercase tracking-[0.13em] text-white/90 transition-colors duration-300 hover:border-white/45 hover:text-white sm:text-[13px] sm:tracking-[0.16em]"
          >
            Company details
          </Link>
        </div>
      </div>

      {/* Scroll Indicator - Bottom Left (magnetic wrapper pulls toward cursor) */}
      <div ref={magneticRef} className="absolute bottom-5 left-5 md:bottom-8 md:left-20">
        <button
          ref={scrollRef}
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
        >
          <span className="text-sm tracking-wider text-white/60">Scroll to Explore</span>
          <svg
            className="h-4 w-4 text-white/60"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>

      {/* Live studio status - Bottom Right, desktop only */}
      <HeroStatusLine className="absolute bottom-8 right-8 hidden text-white/70 md:right-20 md:flex" />

    </section>
  );
}
